<template>
  <div class="mt-4">
    <div v-if="!hasVisibleFields && emptyStateText" class="empty-state">
      {{ emptyStateText }}
    </div>
    <div v-else class="submitted-fields">
      <div v-if="showSubjectField">
        <Field>
          <Label>Subject</Label>
          <TextInput
            :value="textFieldValue('subject')"
            type="text"
            :disabled="isReadonly"
            placeholder="Enter a clear case subject"
            @input="updateField('subject', $event)"
          />
        </Field>
      </div>
      <div v-if="showDescriptionField">
        <Field>
          <Label>Description</Label>
          <TextInput
            :value="textFieldValue('description')"
            type="textarea"
            :disabled="isReadonly"
            placeholder="Enter description"
            @input="updateField('description', $event)"
          />
        </Field>
      </div>
      <div v-if="showCategoryField">
        <Field>
          <Label>Case Submission Type</Label>
          <div class="stk-radios form-radios">
            <label class="stk-radio">
              <input
                :checked="normalizedRequestCategory === 'new_account'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('request_category', 'new_account')"
              >
              New Account
            </label>
            <label class="stk-radio">
              <input
                :checked="normalizedRequestCategory === 'close_account'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('request_category', 'close_account')"
              >
              Close Account
            </label>
            <label class="stk-radio">
              <input
                :checked="normalizedRequestCategory === 'trading_related'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('request_category', 'trading_related')"
              >
              Trading Related
            </label>
            <label class="stk-radio">
              <input
                :checked="normalizedRequestCategory === 'service_other'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('request_category', 'service_other')"
              >
              Service / Other
            </label>
          </div>
        </Field>
      </div>

      <div v-if="showStep2">
        <Field v-if="isServiceOther">
          <Label>Service / Other Request Type</Label>
          <div class="stk-radios form-radios">
            <label class="stk-radio">
              <input
                :checked="fieldValue('service_case_type') === 'orion_request'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('service_case_type', 'orion_request')"
              >
              Orion Request
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('service_case_type') === 'client_record_changes'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('service_case_type', 'client_record_changes')"
              >
              Client Record / Account Changes
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('service_case_type') === 'questions'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('service_case_type', 'questions')"
              >
              Questions
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('service_case_type') === 'business_development'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('service_case_type', 'business_development')"
              >
              Business Development
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('service_case_type') === 'advisor_org_maintenance'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('service_case_type', 'advisor_org_maintenance')"
              >
              Advisor Org Maintenance
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('service_case_type') === 'compliance'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('service_case_type', 'compliance')"
              >
              Compliance
            </label>
          </div>
        </Field>

        <Field v-if="isTradingRelated">
          <Label>Trading Request Type</Label>
          <div class="stk-radios form-radios radios-wide">
            <label class="stk-radio">
              <input
                :checked="isRaiseCashDistributeCase"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('trading_case_type', 'get_cash')"
              >
              Get Cash (Distribute Cash)
            </label>
            <label class="stk-radio">
              <input
                :checked="isCashAmountCase"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('trading_case_type', 'add_cash')"
              >
              Add Cash (Deposit Cash)
            </label>
            <label class="stk-radio">
              <input
                :checked="isTradeRequestCase"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('trading_case_type', 'trade_request_instructions')"
              >
              Trade Request / Instructions
            </label>
            <label class="stk-radio">
              <input
                :checked="isTradeReviewCase"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('trading_case_type', 'trade_review')"
              >
              Trade Review
            </label>
            <label class="stk-radio">
              <input
                :checked="isAdvocacyCase"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('trading_case_type', 'advocacy')"
              >
              Advocacy
            </label>
            <label class="stk-radio">
              <input
                :checked="isIpsRequestCase"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('trading_case_type', 'ips_request')"
              >
              IPS Request
            </label>
            <label class="stk-radio">
              <input
                :checked="isReoptimizationCase"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('trading_case_type', 'reoptimization')"
              >
              Re-Optimization
            </label>
            <label class="stk-radio">
              <input
                :checked="isVadisQuestionCase"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('trading_case_type', 'vadis_question')"
              >
              Vadis Question
            </label>
          </div>
        </Field>
      </div>

      <div v-if="showStep3">
        <MultiSelectChecklistField
          v-if="isNewAccountCase && shouldShowField('new_account_custodian')"
          :value="fieldValue('new_account_custodian')"
          :options="custodianPicklistValues"
          :is-readonly="isReadonly"
          @input="updateField('new_account_custodian', $event)"
        />
        <MultiSelectChecklistField
          v-if="isNewAccountCase && shouldShowField('new_account_account_type')"
          label="Account Type"
          :value="fieldValue('new_account_account_type')"
          :options="accountTypePicklistValues"
          :is-readonly="isReadonly"
          @input="updateField('new_account_account_type', $event)"
        />
        <Field v-if="isNewAccountCase && shouldShowField('new_account_description')">
          <Label>Description</Label>
          <TextInput
            :value="textFieldValue('new_account_description')"
            type="textarea"
            :disabled="isReadonly"
            placeholder="Enter description"
            @input="updateField('new_account_description', $event)"
          />
        </Field>

        <Field v-if="isCloseAccountCase && shouldShowField('close_account_number')">
          <Label>Account Number / Name</Label>
          <TextInput
            :value="textFieldValue('close_account_number')"
            type="text"
            :disabled="isReadonly"
            placeholder="Account number or account name"
            @input="updateField('close_account_number', $event)"
          />
        </Field>
        <MultiSelectChecklistField
          v-if="isCloseAccountCase && shouldShowField('close_account_custodian')"
          :value="fieldValue('close_account_custodian')"
          :options="custodianPicklistValues"
          :is-readonly="isReadonly"
          @input="updateField('close_account_custodian', $event)"
        />
        <Field v-if="isCloseAccountCase && shouldShowField('close_account_sec_reason')">
          <Label>SEC Reason</Label>
          <Select
            class="close-account-sec-reason-select"
            :value="fieldValue('close_account_sec_reason')"
            :options="closeAccountSecReasonOptions"
            :disabled="isReadonly"
            @input="updateField('close_account_sec_reason', $event)"
          />
        </Field>
        <Field v-if="isCloseAccountCase && shouldShowField('close_account_description')">
          <Label>Description</Label>
          <TextInput
            :value="textFieldValue('close_account_description')"
            type="textarea"
            :disabled="isReadonly"
            placeholder="Enter description"
            @input="updateField('close_account_description', $event)"
          />
        </Field>

        <MultiSelectChecklistField
          v-if="isOrionRequestCase && shouldShowField('orion_request_custodian')"
          :value="fieldValue('orion_request_custodian')"
          :options="custodianPicklistValues"
          :is-readonly="isReadonly"
          @input="updateField('orion_request_custodian', $event)"
        />
        <Field v-if="isOrionRequestCase && shouldShowField('orion_request_account_identifier')">
          <Label>Account Number / Name</Label>
          <TextInput
            :value="textFieldValue('orion_request_account_identifier')"
            type="text"
            :disabled="isReadonly"
            placeholder="Account number or account name"
            @input="updateField('orion_request_account_identifier', $event)"
          />
        </Field>
        <Field v-if="isOrionRequestCase && shouldShowField('orion_request_details')">
          <Label>Question</Label>
          <TextInput
            :value="textFieldValue('orion_request_details')"
            type="textarea"
            :disabled="isReadonly"
            placeholder="Enter the question"
            @input="updateField('orion_request_details', $event)"
          />
        </Field>

        <Field v-if="isClientRecordChangeCase && shouldShowField('client_record_change_type')">
          <Label>Client Record Change Type</Label>
          <div class="stk-radios form-radios">
            <label class="stk-radio">
              <input
                :checked="fieldValue('client_record_change_type') === 'beneficiary_change'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('client_record_change_type', 'beneficiary_change')"
              >
              Beneficiary Change
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('client_record_change_type') === 'ias_or_repapering'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('client_record_change_type', 'ias_or_repapering')"
              >
              IAS / Re-papering
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('client_record_change_type') === 'trustee_or_auth_id_change'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('client_record_change_type', 'trustee_or_auth_id_change')"
              >
              Trustee or Auth ID Change
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('client_record_change_type') === 'intercustodial_change'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('client_record_change_type', 'intercustodial_change')"
              >
              Intercustodial Change
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('client_record_change_type') === 'email_address_change'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('client_record_change_type', 'email_address_change')"
              >
              Email Address Change
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('client_record_change_type') === 'address_change'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('client_record_change_type', 'address_change')"
              >
              Address Change
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('client_record_change_type') === 'deceased_client'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('client_record_change_type', 'deceased_client')"
              >
              Deceased Client
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('client_record_change_type') === 'divorce'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('client_record_change_type', 'divorce')"
              >
              Divorce
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('client_record_change_type') === 'advisor_change'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('client_record_change_type', 'advisor_change')"
              >
              Advisor Change
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('client_record_change_type') === 'discontinue_regular_distributions'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('client_record_change_type', 'discontinue_regular_distributions')"
              >
              Maintenance - Discontinue Regular Distributions
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('client_record_change_type') === 'change_recurring_distribution'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('client_record_change_type', 'change_recurring_distribution')"
              >
              Maintenance - Change Recurring Distribution
            </label>
          </div>
        </Field>
        <Field v-if="isClientRecordChangeCase && shouldShowField('client_record_change_account_identifier')">
          <Label>Account Number / Name</Label>
          <TextInput
            :value="textFieldValue('client_record_change_account_identifier')"
            type="text"
            :disabled="isReadonly"
            placeholder="Account number or account name"
            @input="updateField('client_record_change_account_identifier', $event)"
          />
        </Field>
        <MultiSelectChecklistField
          v-if="isClientRecordChangeCase && shouldShowField('client_record_change_custodian')"
          :value="fieldValue('client_record_change_custodian')"
          :options="custodianPicklistValues"
          :is-readonly="isReadonly"
          @input="updateField('client_record_change_custodian', $event)"
        />
        <Field v-if="isClientRecordChangeCase && shouldShowField('client_record_change_details')">
          <Label>Client Record Change Details</Label>
          <TextInput
            :value="textFieldValue('client_record_change_details')"
            type="textarea"
            :disabled="isReadonly"
            placeholder="Describe what should change"
            @input="updateField('client_record_change_details', $event)"
          />
        </Field>

        <Field v-if="isBusinessDevelopmentCase && shouldShowField('business_development_type')">
          <Label>Business Development Type</Label>
          <div class="stk-radios form-radios">
            <label class="stk-radio">
              <input
                :checked="fieldValue('business_development_type') === 'website_content_assistance'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('business_development_type', 'website_content_assistance')"
              >
              Website Content Assistance
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('business_development_type') === 'marketing_materials_request'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('business_development_type', 'marketing_materials_request')"
              >
              Marketing Materials Request
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('business_development_type') === 'question'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('business_development_type', 'question')"
              >
              Question
            </label>
          </div>
        </Field>
        <Field v-if="isBusinessDevelopmentCase && shouldShowField('business_development_details')">
          <Label>Description</Label>
          <TextInput
            :value="textFieldValue('business_development_details')"
            type="textarea"
            :disabled="isReadonly"
            placeholder="Enter details"
            @input="updateField('business_development_details', $event)"
          />
        </Field>

        <Field v-if="isAdvisorOrgMaintenanceCase && shouldShowField('advisor_org_maintenance_type')">
          <Label>Advisor Org Maintenance Type</Label>
          <div class="stk-radios form-radios">
            <label class="stk-radio">
              <input
                :checked="fieldValue('advisor_org_maintenance_type') === 'organization_address_change'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('advisor_org_maintenance_type', 'organization_address_change')"
              >
              Organization Address Change
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('advisor_org_maintenance_type') === 'add_subtract_team_member'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('advisor_org_maintenance_type', 'add_subtract_team_member')"
              >
              Add/Subtract Team Member
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('advisor_org_maintenance_type') === 'billing_accounts_receivable_questions'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('advisor_org_maintenance_type', 'billing_accounts_receivable_questions')"
              >
              Billing / Accounts Receivable Billing Questions
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('advisor_org_maintenance_type') === 'advisor_out_of_office'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('advisor_org_maintenance_type', 'advisor_out_of_office')"
              >
              Advisor Out of Office
            </label>
          </div>
        </Field>
        <Field v-if="isAdvisorOrgMaintenanceCase && shouldShowField('advisor_org_maintenance_details')">
          <Label>Description</Label>
          <TextInput
            :value="textFieldValue('advisor_org_maintenance_details')"
            type="textarea"
            :disabled="isReadonly"
            placeholder="Enter details"
            @input="updateField('advisor_org_maintenance_details', $event)"
          />
        </Field>

        <Field v-if="isQuestionsCase && shouldShowField('questions_case_type')">
          <Label>Questions Type</Label>
          <div class="stk-radios form-radios">
            <label class="stk-radio">
              <input
                :checked="fieldValue('questions_case_type') === 'general_fafn_questions'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('questions_case_type', 'general_fafn_questions')"
              >
              General FAFN Questions
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('questions_case_type') === 'investment_committee'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('questions_case_type', 'investment_committee')"
              >
              Investment Committee
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('questions_case_type') === 'schwab_website_assistance'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('questions_case_type', 'schwab_website_assistance')"
              >
              Schwab Website
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('questions_case_type') === 'goldman_folio_website_assistance'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('questions_case_type', 'goldman_folio_website_assistance')"
              >
              Goldman/Folio Website
            </label>
          </div>
        </Field>
        <Field v-if="isQuestionsCase && shouldShowField('fafn_question')">
          <Label>Question Description</Label>
          <TextInput
            :value="textFieldValue('fafn_question')"
            type="textarea"
            :disabled="isReadonly"
            placeholder="Enter the question"
            @input="updateField('fafn_question', $event)"
          />
        </Field>

        <Field v-if="isComplianceCase && shouldShowField('compliance_case_type')">
          <Label>Compliance Type</Label>
          <div class="stk-radios form-radios">
            <label class="stk-radio">
              <input
                :checked="fieldValue('compliance_case_type') === 'review'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('compliance_case_type', 'review')"
              >
              Review
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('compliance_case_type') === 'fraud_alert'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('compliance_case_type', 'fraud_alert')"
              >
              Fraud Alert
            </label>
          </div>
        </Field>
        <Field v-if="isComplianceCase && shouldShowField('compliance_details')">
          <Label>Description</Label>
          <TextInput
            :value="textFieldValue('compliance_details')"
            type="textarea"
            :disabled="isReadonly"
            placeholder="Enter details"
            @input="updateField('compliance_details', $event)"
          />
        </Field>
        <Field v-if="isComplianceFraudAlertCase && shouldShowField('compliance_account_number')">
          <Label>Account Number</Label>
          <TextInput
            :value="textFieldValue('compliance_account_number')"
            type="text"
            :disabled="isReadonly"
            placeholder="Account number"
            @input="updateField('compliance_account_number', $event)"
          />
        </Field>
        <MultiSelectChecklistField
          v-if="isComplianceFraudAlertCase && shouldShowField('compliance_custodian')"
          :value="fieldValue('compliance_custodian')"
          :options="custodianPicklistValues"
          :is-readonly="isReadonly"
          @input="updateField('compliance_custodian', $event)"
        />

        <Field v-if="isRaiseCashDistributeCase && shouldShowField('distribution_amount')">
          <Label>Distribution Amount</Label>
          <TextInput
            :value="textFieldValue('distribution_amount')"
            type="text"
            :disabled="isReadonly"
            placeholder="Amount"
            @keydown.native="onDecimalKeydown('distribution_amount', $event)"
            @paste.native="onDecimalPaste('distribution_amount', $event)"
            @input="updateDecimalField('distribution_amount', $event)"
          />
        </Field>
        <Field v-if="isRaiseCashDistributeCase && shouldShowField('distribution_account_number')">
          <Label>Distribution Account Number</Label>
          <TextInput
            :value="textFieldValue('distribution_account_number')"
            type="text"
            :disabled="isReadonly"
            placeholder="Account number"
            @input="updateField('distribution_account_number', $event)"
          />
        </Field>
        <Field v-if="isRaiseCashDistributeCase && shouldShowField('distribution_delivery_instructions')">
          <Label>Distribution Delivery Instructions</Label>
          <TextInput
            :value="textFieldValue('distribution_delivery_instructions')"
            type="textarea"
            :disabled="isReadonly"
            placeholder="Delivery instructions"
            @input="updateField('distribution_delivery_instructions', $event)"
          />
        </Field>
        <Field v-if="isRaiseCashDistributeCase && shouldShowField('distribution_modify_recurring')">
          <Label>Modify Recurring Distribution</Label>
          <TextInput
            :value="textFieldValue('distribution_modify_recurring')"
            type="textarea"
            :disabled="isReadonly"
            placeholder="Describe recurring distribution changes"
            @input="updateField('distribution_modify_recurring', $event)"
          />
        </Field>
        <MultiSelectChecklistField
          v-if="isRaiseCashDistributeCase && shouldShowField('distribution_custodian')"
          :value="fieldValue('distribution_custodian')"
          :options="custodianPicklistValues"
          :is-readonly="isReadonly"
          @input="updateField('distribution_custodian', $event)"
        />

        <Field v-if="isCashAmountCase && shouldShowField('cash_transfer_into_existing_account')">
          <Label>Transfer into Existing Account?</Label>
          <div class="stk-radios form-radios">
            <label class="stk-radio">
              <input
                :checked="fieldValue('cash_transfer_into_existing_account') === 'yes'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('cash_transfer_into_existing_account', 'yes')"
              >
              Yes
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('cash_transfer_into_existing_account') === 'no'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('cash_transfer_into_existing_account', 'no')"
              >
              No
            </label>
          </div>
        </Field>
        <Field v-if="isCashAmountCase && shouldShowField('cash_amount')">
          <Label>Amount to Add to Account</Label>
          <TextInput
            :value="textFieldValue('cash_amount')"
            type="text"
            :disabled="isReadonly"
            placeholder="Amount"
            @keydown.native="onDecimalKeydown('cash_amount', $event)"
            @paste.native="onDecimalPaste('cash_amount', $event)"
            @input="updateDecimalField('cash_amount', $event)"
          />
        </Field>
        <Field v-if="isCashAmountCase && shouldShowField('cash_account_number')">
          <Label>Account Number</Label>
          <TextInput
            :value="textFieldValue('cash_account_number')"
            type="text"
            :disabled="isReadonly"
            placeholder="Account number"
            @input="updateField('cash_account_number', $event)"
          />
        </Field>
        <MultiSelectChecklistField
          v-if="isCashAmountCase && shouldShowField('cash_custodian')"
          :value="fieldValue('cash_custodian')"
          :options="custodianPicklistValues"
          :is-readonly="isReadonly"
          @input="updateField('cash_custodian', $event)"
        />
        <Field v-if="isCashAmountCase && shouldShowField('cash_is_recurring')">
          <Label>Is this recurring?</Label>
          <div class="stk-radios form-radios">
            <label class="stk-radio">
              <input
                :checked="fieldValue('cash_is_recurring') === 'yes'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('cash_is_recurring', 'yes')"
              >
              Yes
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('cash_is_recurring') === 'no'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('cash_is_recurring', 'no')"
              >
              No
            </label>
          </div>
        </Field>

        <Field v-if="isTradeRequestCase && shouldShowField('trade_request_type')">
          <Label>Trade Request Type</Label>
          <div class="stk-radios form-radios">
            <label class="stk-radio">
              <input
                :checked="fieldValue('trade_request_type') === 'new_trade_exclusion'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('trade_request_type', 'new_trade_exclusion')"
              >
              New Trade Exclusion
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('trade_request_type') === 'tax_loss_gains_harvesting'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('trade_request_type', 'tax_loss_gains_harvesting')"
              >
              Tax Loss/Gains Harvesting
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('trade_request_type') === 'trade_approval_non_program'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('trade_request_type', 'trade_approval_non_program')"
              >
              Trade Approval (non-Program)
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('trade_request_type') === 'trade_review_gifting_request'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('trade_request_type', 'trade_review_gifting_request')"
              >
              Trade Review & Gifting Request
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('trade_request_type') === 'trade_review_model_change'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('trade_request_type', 'trade_review_model_change')"
              >
              Trade Review - Model Change
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('trade_request_type') === 'trade_request_non_fiduciary'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('trade_request_type', 'trade_request_non_fiduciary')"
              >
              Trade Request - Non-Fiduciary
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('trade_request_type') === 'non_fiduciary'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('trade_request_type', 'non_fiduciary')"
              >
              Non-Fiduciary
            </label>
          </div>
        </Field>
        <Field v-if="isTradeRequestCase && shouldShowField('trade_request_account_numbers')">
          <Label>Trade Request Account Number(s)</Label>
          <TextInput
            :value="textFieldValue('trade_request_account_numbers')"
            type="text"
            :disabled="isReadonly"
            placeholder="Account number(s)"
            @input="updateField('trade_request_account_numbers', $event)"
          />
        </Field>
        <Field v-if="isTradeRequestCase && shouldShowField('trade_request_instructions')">
          <Label>Trade Instructions</Label>
          <TextInput
            :value="textFieldValue('trade_request_instructions')"
            type="textarea"
            :disabled="isReadonly"
            placeholder="Trade instructions"
            @input="updateField('trade_request_instructions', $event)"
          />
        </Field>
        <MultiSelectChecklistField
          v-if="isTradeRequestCase && shouldShowField('trade_request_custodian')"
          :value="fieldValue('trade_request_custodian')"
          :options="custodianPicklistValues"
          :is-readonly="isReadonly"
          @input="updateField('trade_request_custodian', $event)"
        />

        <Field v-if="isIpsRequestCase && shouldShowField('ips_request_type')">
          <Label>IPS Request Type</Label>
          <div class="stk-radios form-radios">
            <label class="stk-radio">
              <input
                :checked="fieldValue('ips_request_type') === 'ips_review'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('ips_request_type', 'ips_review')"
              >
              IPS Review
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('ips_request_type') === 'pre_ips_question'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('ips_request_type', 'pre_ips_question')"
              >
              Pre-IPS Question
            </label>
          </div>
        </Field>
        <Field v-if="isIpsRequestCase && shouldShowField('ips_account_number')">
          <Label>Account Number</Label>
          <TextInput
            :value="textFieldValue('ips_account_number')"
            type="text"
            :disabled="isReadonly"
            placeholder="Account number"
            @input="updateField('ips_account_number', $event)"
          />
        </Field>
        <MultiSelectChecklistField
          v-if="isIpsRequestCase && shouldShowField('ips_custodian')"
          :value="fieldValue('ips_custodian')"
          :options="custodianPicklistValues"
          :is-readonly="isReadonly"
          @input="updateField('ips_custodian', $event)"
        />
        <Field v-if="isIpsRequestCase && shouldShowField('ips_request_details')">
          <Label>Pre IPS Questions</Label>
          <TextInput
            :value="textFieldValue('ips_request_details')"
            type="textarea"
            :disabled="isReadonly"
            placeholder="Enter IPS request details"
            @input="updateField('ips_request_details', $event)"
          />
        </Field>

        <Field v-if="isTradeReviewCase && shouldShowField('trade_review_type')">
          <Label>Trade Review Type</Label>
          <div class="stk-radios form-radios">
            <label class="stk-radio">
              <input
                :checked="fieldValue('trade_review_type') === 'accounts_review_discussion_request'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('trade_review_type', 'accounts_review_discussion_request')"
              >
              Accounts Review (Discussion Request)
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('trade_review_type') === 'performance_review_quarterly_reports'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('trade_review_type', 'performance_review_quarterly_reports')"
              >
              Performance Review/Quarterly Reports
            </label>
            <label class="stk-radio">
              <input
                :checked="fieldValue('trade_review_type') === 'manager_profile_requests'"
                type="radio"
                :disabled="isReadonly"
                @change="onRadioChange('trade_review_type', 'manager_profile_requests')"
              >
              Manager Profile requests
            </label>
          </div>
        </Field>
        <Field v-if="isTradeReviewCase && shouldShowField('trade_review_account_number')">
          <Label>Account Number</Label>
          <TextInput
            :value="textFieldValue('trade_review_account_number')"
            type="text"
            :disabled="isReadonly"
            placeholder="Account number"
            @input="updateField('trade_review_account_number', $event)"
          />
        </Field>
        <MultiSelectChecklistField
          v-if="isTradeReviewCase && shouldShowField('trade_review_custodian')"
          :value="fieldValue('trade_review_custodian')"
          :options="custodianPicklistValues"
          :is-readonly="isReadonly"
          @input="updateField('trade_review_custodian', $event)"
        />
        <Field v-if="isTradeReviewCase && shouldShowField('trade_review_instructions')">
          <Label>Instructions</Label>
          <TextInput
            :value="textFieldValue('trade_review_instructions')"
            type="textarea"
            :disabled="isReadonly"
            placeholder="Enter trade review instructions"
            @input="updateField('trade_review_instructions', $event)"
          />
        </Field>

        <Field v-if="isAdvocacyCase && shouldShowField('advocacy_account_number')">
          <Label>Account Number</Label>
          <TextInput
            :value="textFieldValue('advocacy_account_number')"
            type="text"
            :disabled="isReadonly"
            placeholder="Account number"
            @input="updateField('advocacy_account_number', $event)"
          />
        </Field>
        <MultiSelectChecklistField
          v-if="isAdvocacyCase && shouldShowField('advocacy_custodian')"
          :value="fieldValue('advocacy_custodian')"
          :options="custodianPicklistValues"
          :is-readonly="isReadonly"
          @input="updateField('advocacy_custodian', $event)"
        />
        <Field v-if="isAdvocacyCase && shouldShowField('advocacy_instruction')">
          <Label>Instruction</Label>
          <TextInput
            :value="textFieldValue('advocacy_instruction')"
            type="textarea"
            :disabled="isReadonly"
            placeholder="Enter instruction"
            @input="updateField('advocacy_instruction', $event)"
          />
        </Field>

        <Field v-if="isReoptimizationCase && shouldShowField('reoptimization_account_identifier')">
          <Label>Account Number / Name</Label>
          <TextInput
            :value="textFieldValue('reoptimization_account_identifier')"
            type="text"
            :disabled="isReadonly"
            placeholder="Account number or account name"
            @input="updateField('reoptimization_account_identifier', $event)"
          />
        </Field>
        <MultiSelectChecklistField
          v-if="isReoptimizationCase && shouldShowField('reoptimization_custodian')"
          :value="fieldValue('reoptimization_custodian')"
          :options="custodianPicklistValues"
          :is-readonly="isReadonly"
          @input="updateField('reoptimization_custodian', $event)"
        />
        <Field v-if="isReoptimizationCase && shouldShowField('reoptimization_instruction')">
          <Label>Instruction</Label>
          <TextInput
            :value="textFieldValue('reoptimization_instruction')"
            type="textarea"
            :disabled="isReadonly"
            placeholder="Enter instruction"
            @input="updateField('reoptimization_instruction', $event)"
          />
        </Field>
        <Field v-if="isVadisQuestionCase && shouldShowField('vadis_question')">
          <Label>Vadis Question(s)</Label>
          <TextInput
            :value="textFieldValue('vadis_question')"
            type="textarea"
            :disabled="isReadonly"
            placeholder="Enter the question"
            @input="updateField('vadis_question', $event)"
          />
        </Field>

        <p v-if="!showDetailFields && !(isReadonly && showOnlyPopulated)" class="hint">
          No additional details are required for this request type.
        </p>
      </div>

      <div v-if="showPriorityField">
        <Field>
          <Label>Priority</Label>
          <div class="stk-radios form-radios priority-radios">
            <label class="stk-radio">
              <input
                :checked="normalizedPriority === 'low'"
                type="radio"
                :disabled="isReadonly"
                @change="updatePriority('Low')"
              >
              Low
            </label>
            <label class="stk-radio">
              <input
                :checked="normalizedPriority === 'medium'"
                type="radio"
                :disabled="isReadonly"
                @change="updatePriority('Medium')"
              >
              Medium
            </label>
            <label class="stk-radio">
              <input
                :checked="normalizedPriority === 'high'"
                type="radio"
                :disabled="isReadonly"
                @change="updatePriority('High')"
              >
              High
            </label>
          </div>
        </Field>
      </div>
    </div>
  </div>
</template>

<script>
import {
  Field,
  Label,
  Select,
  TextInput,
} from '@stakeui';
import MultiSelectChecklistField from '@frontend/ui/features/example/components/MultiSelectChecklistField.vue';

const CLOSE_ACCOUNT_SEC_REASON_VALUES = [
  'Unknown',
  'Account opened/Never funded',
  'Account type no longer supported/Compliance reasons',
  'Alert from Custodian/initiated by client/notified by contra firm',
  'Debt or Mortgage payoff',
  'Different Advisor',
  'Distributing account/retirement - account funds fully distributed',
  'Employee leaving the company',
  'End-User Client notified FAFN',
  'Fees too high',
  'Large Purchase/Other Cash Needs',
  'Market Concerns',
  'No agreement in place for Non-Program accounts',
  'No Reason Given/Client unresponsive',
  'Not happy with Performance/Returns',
  'Reconciliation by Quarterly Reports team',
  'Reconciliation shows an account closed at Schwab',
  'Self-Manage/Delink requested',
  'Trade Error',
  'Transition - Age of Majority',
  'Transition - Deceased/Division of Assets',
  'Transition - Divorce',
  'Transition - Non-Taxable Conversion',
  'Transition - Program or Registration Change/Moving to another FA Account',
  'Transition - Rename in Axys',
  'Transition - Revocation of Living Trust',
  'Transition - Roth Conversion',
  'Transition - Transfer to another Custodian',
  'Transition - Trust Creation',
];

const CUSTODIAN_PICKLIST_VALUES = [
  'Apex',
  'Goldman Sachs',
  'Schwab',
  'SEI',
];

const ACCOUNT_TYPE_PICKLIST_VALUES = [
  'Individual',
  'Inherited IRA',
  'Inherited Roth IRA',
  'Rollover IRA',
  'Joint',
  'Roth IRA',
  'Traditional IRA',
  'Transfer on Death (TOD)',
  'SEP IRA',
  'Simple IRA',
  'Trust',
  'Other - Custodian Dependant',
];

export default {
  name: 'CaseRequestFields',
  components: {
    MultiSelectChecklistField,
    Field,
    Label,
    Select,
    TextInput,
  },
  props: {
    fields: {
      type: Object,
      default: () => ({}),
    },
    priority: {
      type: String,
      default: '',
    },
    mode: {
      type: String,
      default: 'edit',
    },
    showOnlyPopulated: {
      type: Boolean,
      default: false,
    },
    emptyStateText: {
      type: String,
      default: '',
    },
  },
  computed: {
    isReadonly() {
      return this.mode === 'readonly';
    },
    showSubjectField() {
      return !this.showOnlyPopulated || !this.isBlank(this.fieldValue('subject'));
    },
    showDescriptionField() {
      if (!this.isReadonly) {
        return false;
      }
      return !this.showOnlyPopulated || !this.isBlank(this.fieldValue('description'));
    },
    normalizedRequestCategory() {
      const requestCategory = this.fieldValue('request_category');
      if (requestCategory === 'trading_related') {
        return 'trading_related';
      }
      if (requestCategory === 'new_account' || requestCategory === 'close_account' || requestCategory === 'service_other') {
        return requestCategory;
      }
      const serviceCaseType = this.fieldValue('service_case_type');
      if (serviceCaseType === 'new_account') {
        return 'new_account';
      }
      if (serviceCaseType === 'close_account') {
        return 'close_account';
      }
      if (!this.isBlank(serviceCaseType) || requestCategory === 'service_maintenance') {
        return 'service_other';
      }
      if (!this.isBlank(this.fieldValue('trading_case_type'))) {
        return 'trading_related';
      }
      return '';
    },
    hasRequestCategory() {
      return !this.isBlank(this.normalizedRequestCategory);
    },
    showCategoryField() {
      return !this.showOnlyPopulated || this.hasRequestCategory;
    },
    showStep2() {
      if (!this.hasRequestCategory) {
        return false;
      }
      if (this.isServiceOther) {
        return !this.showOnlyPopulated || !this.isBlank(this.fieldValue('service_case_type'));
      }
      if (this.isTradingRelated) {
        return !this.showOnlyPopulated || !this.isBlank(this.fieldValue('trading_case_type'));
      }
      return false;
    },
    showStep3() {
      if (!this.hasRequestCategory) {
        return false;
      }
      if (this.isNewAccountCase || this.isCloseAccountCase) {
        return true;
      }
      if (this.isServiceOther) {
        return !this.isBlank(this.fieldValue('service_case_type'));
      }
      if (this.isTradingRelated) {
        return !this.isBlank(this.fieldValue('trading_case_type'));
      }
      return false;
    },
    showPriorityField() {
      if (this.isReadonly) {
        return !this.showOnlyPopulated || !this.isBlank(this.priority);
      }
      return this.showStep3;
    },
    normalizedPriority() {
      return String(this.priority || '').trim().toLowerCase();
    },
    isServiceOther() {
      return this.normalizedRequestCategory === 'service_other';
    },
    isTradingRelated() {
      return this.normalizedRequestCategory === 'trading_related';
    },
    isNewAccountCase() {
      return this.normalizedRequestCategory === 'new_account' ||
        this.fieldValue('service_case_type') === 'new_account';
    },
    isCloseAccountCase() {
      return this.normalizedRequestCategory === 'close_account' ||
        this.fieldValue('service_case_type') === 'close_account';
    },
    isOrionRequestCase() {
      return this.fieldValue('service_case_type') === 'orion_request';
    },
    isClientRecordChangeCase() {
      return this.fieldValue('service_case_type') === 'client_record_changes';
    },
    isBusinessDevelopmentCase() {
      return this.fieldValue('service_case_type') === 'business_development';
    },
    isAdvisorOrgMaintenanceCase() {
      return this.fieldValue('service_case_type') === 'advisor_org_maintenance';
    },
    isQuestionsCase() {
      return this.fieldValue('service_case_type') === 'questions';
    },
    isComplianceCase() {
      return this.fieldValue('service_case_type') === 'compliance';
    },
    isComplianceFraudAlertCase() {
      return this.fieldValue('compliance_case_type') === 'fraud_alert';
    },
    isRaiseCashDistributeCase() {
      return ['raise_cash_distribute', 'get_cash'].includes(this.fieldValue('trading_case_type'));
    },
    isCashAmountCase() {
      return this.fieldValue('trading_case_type') === 'add_cash';
    },
    isTradeRequestCase() {
      return this.fieldValue('trading_case_type') === 'trade_request_instructions';
    },
    isIpsRequestCase() {
      return this.fieldValue('trading_case_type') === 'ips_request';
    },
    isTradeReviewCase() {
      return this.fieldValue('trading_case_type') === 'trade_review';
    },
    isAdvocacyCase() {
      return this.fieldValue('trading_case_type') === 'advocacy';
    },
    isReoptimizationCase() {
      return this.fieldValue('trading_case_type') === 'reoptimization';
    },
    isVadisQuestionCase() {
      return this.fieldValue('trading_case_type') === 'vadis_question';
    },
    closeAccountSecReasonOptions() {
      const options = [
        { value: '', label: 'Select SEC reason' },
        ...CLOSE_ACCOUNT_SEC_REASON_VALUES.map(value => ({ value, label: value })),
      ];
      const currentValue = this.fieldValue('close_account_sec_reason');
      const isKnownValue = CLOSE_ACCOUNT_SEC_REASON_VALUES.includes(currentValue);
      if (!this.isBlank(currentValue) && !isKnownValue) {
        options.push({ value: currentValue, label: currentValue });
      }
      return options;
    },
    custodianPicklistValues() {
      return CUSTODIAN_PICKLIST_VALUES;
    },
    accountTypePicklistValues() {
      return ACCOUNT_TYPE_PICKLIST_VALUES;
    },
    showDetailFields() {
      return [
        this.shouldRenderGroup(this.isNewAccountCase, [
          'new_account_custodian',
          'new_account_account_type',
          'new_account_description',
        ]),
        this.shouldRenderGroup(this.isCloseAccountCase, [
          'close_account_number',
          'close_account_custodian',
          'close_account_sec_reason',
          'close_account_description',
        ]),
        this.shouldRenderGroup(this.isOrionRequestCase, [
          'orion_request_custodian',
          'orion_request_account_identifier',
          'orion_request_details',
        ]),
        this.shouldRenderGroup(this.isClientRecordChangeCase, [
          'client_record_change_type',
          'client_record_change_account_identifier',
          'client_record_change_custodian',
          'client_record_change_details',
        ]),
        this.shouldRenderGroup(this.isBusinessDevelopmentCase, [
          'business_development_type',
          'business_development_details',
        ]),
        this.shouldRenderGroup(this.isAdvisorOrgMaintenanceCase, [
          'advisor_org_maintenance_type',
          'advisor_org_maintenance_details',
        ]),
        this.shouldRenderGroup(this.isQuestionsCase, [
          'questions_case_type',
          'fafn_question',
        ]),
        this.shouldRenderGroup(this.isComplianceCase, [
          'compliance_case_type',
          'compliance_details',
          'compliance_account_number',
          'compliance_custodian',
        ]),
        this.shouldRenderGroup(this.isRaiseCashDistributeCase, [
          'distribution_amount',
          'distribution_account_number',
          'distribution_delivery_instructions',
          'distribution_modify_recurring',
          'distribution_custodian',
        ]),
        this.shouldRenderGroup(this.isCashAmountCase, [
          'cash_transfer_into_existing_account',
          'cash_amount',
          'cash_account_number',
          'cash_custodian',
          'cash_is_recurring',
        ]),
        this.shouldRenderGroup(this.isTradeRequestCase, [
          'trade_request_type',
          'trade_request_account_numbers',
          'trade_request_instructions',
          'trade_request_custodian',
        ]),
        this.shouldRenderGroup(this.isIpsRequestCase, [
          'ips_request_type',
          'ips_account_number',
          'ips_custodian',
          'ips_request_details',
        ]),
        this.shouldRenderGroup(this.isTradeReviewCase, [
          'trade_review_type',
          'trade_review_account_number',
          'trade_review_custodian',
          'trade_review_instructions',
        ]),
        this.shouldRenderGroup(this.isAdvocacyCase, [
          'advocacy_account_number',
          'advocacy_custodian',
          'advocacy_instruction',
        ]),
        this.shouldRenderGroup(this.isReoptimizationCase, [
          'reoptimization_account_identifier',
          'reoptimization_custodian',
          'reoptimization_instruction',
        ]),
        this.shouldRenderGroup(this.isVadisQuestionCase, ['vadis_question']),
      ].some(Boolean);
    },
    hasVisibleFields() {
      return this.showSubjectField ||
        this.showDescriptionField ||
        this.showCategoryField ||
        this.showStep2 ||
        this.showDetailFields ||
        this.showPriorityField;
    },
  },
  methods: {
    sanitizeDecimalInput(value) {
      const normalized = String(value ?? '').replace(/[^\d.]/g, '');
      const [whole = '', ...decimalParts] = normalized.split('.');
      if (!decimalParts.length) {
        return whole;
      }
      return `${whole}.${decimalParts.join('')}`;
    },
    shouldRenderGroup(isGroupActive, fieldNames) {
      if (!isGroupActive) {
        return false;
      }
      if (!this.showOnlyPopulated) {
        return true;
      }
      return fieldNames.some(name => !this.isBlank(this.fieldValue(name)));
    },
    shouldShowField(fieldName) {
      if (!this.showOnlyPopulated) {
        return true;
      }
      return !this.isBlank(this.fieldValue(fieldName));
    },
    fieldValue(fieldName) {
      return this.fields?.[fieldName] || '';
    },
    textFieldValue(fieldName) {
      if (!this.isReadonly) {
        return this.fieldValue(fieldName);
      }
      return this.formatSubmittedValue(this.fieldValue(fieldName));
    },
    updateField(fieldName, value) {
      if (this.isReadonly) {
        return;
      }
      this.$emit('update:field', { fieldName, value });
    },
    updateDecimalField(fieldName, value) {
      this.updateField(fieldName, this.sanitizeDecimalInput(value));
    },
    onDecimalKeydown(fieldName, event) {
      if (this.isReadonly) {
        return;
      }

      if (event.ctrlKey || event.metaKey || event.altKey) {
        return;
      }

      const allowedControlKeys = [
        'Backspace',
        'Delete',
        'Tab',
        'Enter',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Home',
        'End',
      ];

      if (allowedControlKeys.includes(event.key)) {
        return;
      }

      if (/^\d$/.test(event.key)) {
        return;
      }

      if (event.key === '.') {
        const currentValue = String(this.fieldValue(fieldName) || '');
        if (!currentValue.includes('.')) {
          return;
        }
      }

      event.preventDefault();
    },
    onDecimalPaste(fieldName, event) {
      if (this.isReadonly) {
        return;
      }
      event.preventDefault();
      const pastedText = event.clipboardData?.getData('text') || '';
      this.updateField(fieldName, this.sanitizeDecimalInput(pastedText));
    },
    updatePriority(value) {
      if (this.isReadonly) {
        return;
      }
      this.$emit('update:priority', value);
    },
    onRadioChange(fieldName, value) {
      this.updateField(fieldName, value);
    },
    isBlank(value) {
      return value === null || value === undefined || String(value).trim() === '';
    },
    formatSubmittedValue(value) {
      if (Array.isArray(value)) {
        return value.join(', ');
      }
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }
      const normalized = String(value);
      if (normalized === 'yes') {
        return 'Yes';
      }
      if (normalized === 'no') {
        return 'No';
      }
      return normalized;
    },
  },
};
</script>

<style scoped>
.submitted-fields {
  display: grid;
  gap: var(--stk-size-3);
}

.submitted-fields > div {
  display: grid;
  gap: var(--stk-size-4);
}

.form-radios {
  display: grid;
  gap: var(--stk-size-2);
}

.form-radios input[type='radio'] {
  accent-color: var(--stk-primary);
}

.close-account-sec-reason-select :deep(.stk-select) {
  border: 1px solid var(--stk-color-border);
  border-radius: var(--stk-radius-2);
}

.priority-radios {
  grid-template-columns: 1fr;
}

.radios-wide {
  grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
  gap: var(--stk-size-3);
}

.hint,
.empty-state {
  color: var(--stk-color-text-muted);
  font-size: 0.85rem;
  margin-top: var(--stk-size-2);
}

.empty-state {
  padding: var(--stk-size-4) 0;
}
</style>
